import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'
import { type LossRunClaimCoverageType, type LossRunClaimStatus } from './lossRunRequest'

// ---- Types ----

export interface DashboardEventSegment {
  text: string
  href?: string
}

export interface DashboardEvent {
  id: string
  date: string
  icon?: string
  cta?: {
    text: string
    type?: 'telematics'
    href?: string
  }
  segments: DashboardEventSegment[]
}

export interface DashboardEventsResponse {
  events: DashboardEvent[]
}

export type RiskLevel = 'low' | 'medium' | 'high'

export interface RiskDriver {
  id: string
  initials: string
  name: string
  bgColor: string
  incidentCount?: number
}

export interface ClaimFactor {
  id: string
  date: string
  claimId: string
}

export interface ViolationFactor {
  id: string
  code: string
  description: string
  count: number
}

export interface PremiumEstimate {
  policyId: string
  policyType: string
  insurer: string
  changePercent: number
  estimatedAsOf: string
  renewalDate: string
}

export interface RiskSnapshotResponse {
  dataSources: { cab: boolean; terminal: boolean }
  riskScore: number
  riskLevel: RiskLevel
  riskDescription: string
  premiumEstimate: PremiumEstimate | null
  highRiskDrivers: RiskDriver[]
  claimFactors: ClaimFactor[]
  violationFactors: ViolationFactor[]
}

export interface DashboardDataSources {
  cab: boolean
  terminal: boolean
  lossRuns?: boolean
}

export enum SafetySnapshotRating {
  ON_TRACK = 'ON_TRACK',
  WORTH_ATTENTION = 'WORTH_ATTENTION',
  NEEDS_ATTENTION = 'NEEDS_ATTENTION',
}

export enum SafetySnapshotStatus {
  READY = 'ready',
  GENERATING = 'generating',
  UNAVAILABLE = 'unavailable',
}

export type MetricSeverity = 'good' | 'warn' | 'bad'

export interface SafetyHeadlineMetric {
  value: string
  label: string
  severity: MetricSeverity
}

export interface SafetyFocusItem {
  title: string
  detail: string
}

export interface SafetySnapshotResponse {
  dataSources: DashboardDataSources
  status: SafetySnapshotStatus
  rating: SafetySnapshotRating | null
  riskScore: number | null
  asOf: string | null
  generatedAt?: string | null
  headlineMetrics: SafetyHeadlineMetric[]
  verdictHeadline: string
  verdictBody: string
  focusItems: SafetyFocusItem[]
  premiumNote: string
  footnote: string
}

/** A loss-run file Cai finished reading without finding any claims history in it. */
export interface UnreadLossRunUpload {
  fileName: string
  /** False when Cai's document reader identified the file as something else, e.g. a quote or binder. */
  looksLikeLossRun: boolean
}

export interface ClaimsHistoryResponse {
  dataSources: DashboardDataSources
  borSigned: boolean
  /** Cai is reading uploaded loss runs right now. */
  parsing: boolean
  /** The newest upload that gave nothing, while nothing is on file. */
  unreadUpload: UnreadLossRunUpload | null
  onFile: boolean
  claimsCount: number
  openCount: number
  closedCount: number
  yearsCovered: number
  periodStart: string | null
  periodEnd: string | null
  lastIncidentDate: string | null
  totalIncurred: number | null
  providers: string[]
  latestApplicationId: string | null
  /** The BOR can't be signed until the customer confirms their email in the application chat. */
  emailVerificationRequired: boolean
}

export enum AccidentViolationSource {
  LOSS_RUN = 'loss_run',
  CAB = 'cab',
  TERMINAL = 'terminal',
}

/**
 * One row of the violations feed. Loss-run claims are always served; FMCSA (CAB)
 * and telematics rows only appear when their backend flag
 * (VIOLATIONS_INCLUDE_FMCSA / VIOLATIONS_INCLUDE_TELEMATICS) is on for the org,
 * so every source-specific field is optional.
 */
export interface AccidentViolationItem {
  id: string
  type: string
  title: string
  description: string
  date: string
  source: AccidentViolationSource
  driverName?: string | null
  // Loss-run claim fields
  claimNumber?: string | null
  claimReference?: string | null
  claimCount?: number | null
  claimStatus?: LossRunClaimStatus
  coverageType?: LossRunClaimCoverageType
  coverageLabel?: string | null
  paidLoss?: number | null
  reserve?: number | null
  allocatedExpense?: number | null
  totalIncurred?: number | null
  insurer?: string | null
  policyNumber?: string | null
  policyPeriodStart?: string | null
  policyPeriodEnd?: string | null
  reportedOn?: string | null
  // Telematics fields
  frontMediaAvailable?: boolean
  vehicleVin?: string
  latitude?: number
  longitude?: number
  // FMCSA crash fields
  location?: string
  city?: string
  state?: string
  time?: string
  fatalities?: number
  injuries?: number
  towAway?: boolean
  vehiclesInCrash?: number
  // FMCSA inspection/violation fields
  reportState?: string
  inspectionLevel?: number
  violationOos?: boolean
  inspectionOos?: boolean
  basicCategory?: string
}

export interface AccidentsViolationsResponse {
  dataSources: DashboardDataSources
  items: AccidentViolationItem[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface DriversRiskResponse {
  dataSources: { cab: boolean; terminal: boolean }
  highRisk: RiskDriver[]
  bestPerforming: RiskDriver[]
}

// ---- API functions ----

function getDashboardEvents(): Promise<DashboardEventsResponse> {
  return axios
    .get('/api/v1/insure/dashboard/events/')
    .then((response) => toCamelCase(response.data) as DashboardEventsResponse)
}

function getRiskSnapshot(): Promise<RiskSnapshotResponse> {
  return axios
    .get('/api/v1/insure/dashboard/risk-snapshot/')
    .then((response) => toCamelCase(response.data) as RiskSnapshotResponse)
}

function getAccidentsViolations(page: number = 1): Promise<AccidentsViolationsResponse> {
  return axios
    .get('/api/v1/insure/dashboard/accidents-violations/', { params: { page } })
    .then((response) => toCamelCase(response.data) as AccidentsViolationsResponse)
}

function getAccidentsViolation(id: string): Promise<AccidentViolationItem> {
  return axios
    .get(`/api/v1/insure/dashboard/accidents-violations/${encodeURIComponent(id)}/`)
    .then((response) => toCamelCase(response.data) as AccidentViolationItem)
}

function getDriversRisk(): Promise<DriversRiskResponse> {
  return axios
    .get('/api/v1/insure/dashboard/drivers-risk/')
    .then((response) => toCamelCase(response.data) as DriversRiskResponse)
}

function getSafetySnapshot(): Promise<SafetySnapshotResponse> {
  return axios
    .get('/api/v1/insure/dashboard/safety-snapshot/')
    .then((response) => toCamelCase(response.data) as SafetySnapshotResponse)
}

function getClaimsHistory(): Promise<ClaimsHistoryResponse> {
  return axios
    .get('/api/v1/insure/dashboard/claims-history/')
    .then((response) => toCamelCase(response.data) as ClaimsHistoryResponse)
}

export interface CameraMediaFacing {
  sourceId: string
  videoUrl: string
}

export interface CameraMediaResponse {
  frontFacing?: CameraMediaFacing
  rearFacing?: CameraMediaFacing
}

function getEventCameraMedia(eventId: string): Promise<CameraMediaResponse> {
  return axios
    .get(`/api/v1/insure/dashboard/safety-events/${eventId}/camera-media/`)
    .then((response) => toCamelCase(response.data) as CameraMediaResponse)
}

// ---- Hooks ----

export function useDashboardEvents() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [CacheKeys.DASHBOARD_EVENTS],
    queryFn: getDashboardEvents,
  })
  return { events: data?.events ?? [], isLoading, refetch }
}

export function useRiskSnapshot({ enabled = true }: { enabled?: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.DASHBOARD_RISK],
    queryFn: getRiskSnapshot,
    enabled,
  })
  return { data, isLoading }
}

export function useAccidentsViolations(page: number = 1) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.DASHBOARD_ACCIDENTS, page],
    queryFn: () => getAccidentsViolations(page),
  })
  return {
    items: data?.items ?? [],
    dataSources: data?.dataSources,
    total: data?.total ?? 0,
    page: data?.page ?? page,
    totalPages: data?.totalPages ?? 0,
    isLoading,
  }
}

function isNotFound(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404
}

/**
 * One feed row, for a detail page reached by refresh or deep link. Separates a
 * genuine 404 from a failed request so the page doesn't tell someone their claim
 * doesn't exist when the server merely fell over.
 */
export function useAccidentsViolation(id: string | undefined, { enabled = true } = {}) {
  const { data, isLoading, error } = useQuery({
    queryKey: [CacheKeys.DASHBOARD_ACCIDENTS, 'item', id],
    queryFn: () => getAccidentsViolation(id!),
    enabled: enabled && !!id,
    retry: (failureCount, err) => !isNotFound(err) && failureCount < 2,
  })
  return { item: data, isLoading, loadFailed: !!error && !isNotFound(error) }
}

export function useEventCameraMedia(eventId: string | null) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.DASHBOARD_ACCIDENTS, 'camera-media', eventId],
    queryFn: () => getEventCameraMedia(eventId!),
    enabled: !!eventId,
  })
  return { data, isLoading }
}

export function useSafetySnapshot({ enabled = true }: { enabled?: boolean } = {}) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.DASHBOARD_SAFETY_SNAPSHOT],
    queryFn: getSafetySnapshot,
    enabled,
    // While the LLM narrative is generating, poll until it's ready
    refetchInterval: (query) =>
      query.state.data?.status === SafetySnapshotStatus.GENERATING ? 15_000 : false,
  })
  return { data, isLoading }
}

export interface RefreshSafetySnapshotResponse {
  dispatched: boolean
  status: SafetySnapshotStatus
}

export function useRefreshSafetySnapshot() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      axios
        .post('/api/v1/insure/dashboard/safety-snapshot/refresh/')
        .then((res) => toCamelCase(res.data) as RefreshSafetySnapshotResponse),
    // The next GET reports "generating", which starts the card's polling.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CacheKeys.DASHBOARD_SAFETY_SNAPSHOT] }),
  })
}

export function useClaimsHistory({ enabled = true }: { enabled?: boolean } = {}) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.DASHBOARD_CLAIMS_HISTORY],
    queryFn: getClaimsHistory,
    enabled,
    // The parse finishes in a worker after the upload returns, so keep asking until it lands.
    refetchInterval: (query) => (query.state.data?.parsing ? 10_000 : false),
  })
  return { data, isLoading }
}

export function useDriversRisk() {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.DASHBOARD_DRIVERS_RISK],
    queryFn: getDriversRisk,
  })
  return { data, isLoading }
}
